import {
  permissionSnapshotPartitionIndex,
  type SearchPartitionDigests,
} from '@streamlain/permission-snapshot-contract';

import type { AppliedSearchPartitionDigests } from './search-partition-reconciler';

/** What the server search can leave out because this device has it. */
export interface SearchSourcePlan {
  /** Partitions whose pages are all local; the server leaves them out. */
  readonly excludedPartitions: readonly number[];
  /**
   * Partitions the server lists whose digest this device applied: their
   * mails are all in the local index, so the local index answers them.
   */
  readonly currentPartitions: ReadonlySet<number>;
}

/**
 * A page is complete locally when it is in the warm cache and its partition
 * is current: the digest applied last equals the server's digest, or the
 * server holds no readable page there. A partition is left out of the
 * server search only when every page of it is complete locally.
 */
export function planSearchSources(input: {
  serverDigests: SearchPartitionDigests;
  applied: AppliedSearchPartitionDigests;
  /** Every page of the workspace this device knows. */
  docIds: Iterable<string>;
  inWarmCache(docId: string): boolean;
}): SearchSourcePlan {
  const serverDigest = new Map(
    input.serverDigests.partitions.map(p => [p.partitionIndex, p.digest])
  );
  const current = (partition: number) => {
    const digest = serverDigest.get(partition);
    return digest === undefined || input.applied[String(partition)] === digest;
  };
  const notLocal = new Set<number>();
  for (const docId of input.docIds) {
    const partition = permissionSnapshotPartitionIndex('doc', docId);
    if (!current(partition) || !input.inWarmCache(docId))
      notLocal.add(partition);
  }
  return {
    excludedPartitions: [...serverDigest.keys()].filter(
      partition => current(partition) && !notLocal.has(partition)
    ),
    currentPartitions: new Set(
      [...serverDigest].flatMap(([partition, digest]) =>
        input.applied[String(partition)] === digest ? [partition] : []
      )
    ),
  };
}
